import React from "react";
import Dialog from "../../../components/Dialog";
import Button from "../../../components/Button";
import { TrendingUp, Percent, AlertCircle, PieChart, DollarSign, CheckCircle2, XCircle } from "lucide-react";

const BrokerDetailsModal = ({ open, onClose, broker, details }) => {
  if (!broker) return null;

  const d = details || {};

  const Section = ({ icon: Icon, title, children }) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3 text-primary-600">
        <Icon className="w-4 h-4" />
        <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>
      </div>
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        {children}
      </div>
    </div>
  );

  const StatItem = ({ label, value, colorClass = "text-gray-900" }) => (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 mb-0.5">{label}</span>
      <span className={`font-semibold ${colorClass}`}>{value || "—"}</span>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${broker.name} Details`}
      size="lg"
      footer={
        <Button variant="primary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-2">
        {/* Header Summary */}
        <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-2xl mb-6">
          <div className="text-4xl">{broker.logo || "📊"}</div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{broker.name}</h2>
            <div className="flex items-center gap-2 text-amber-600 font-medium">
              {d.rating || "—"} ★ <span className="text-gray-400 text-sm font-normal">Rating</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {/* Overview */}
          <Section icon={TrendingUp} title="Market Overview">
            <div className="grid grid-cols-2 gap-4">
              <StatItem label="Active Clients" value={d.activeClients} />
              <StatItem label="Trend" value={d.activeClientsTrend} colorClass={d.activeClientsTrend === "Growing" ? "text-success-600" : "text-gray-900"} />
              <StatItem label="Brokerage" value={d.brokerage} />
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <span className="text-xs text-gray-500 mb-1 block">Description</span>
              <p className="text-sm text-gray-700 leading-relaxed">{d.description}</p>
            </div>
          </Section>

          {/* Charges */}
          <Section icon={Percent} title="Charges & Fees">
            <div className="grid grid-cols-3 gap-4">
              <StatItem label="Delivery" value={d.charges?.delivery} />
              <StatItem label="Intraday" value={d.charges?.intraday} />
              <StatItem label="Futures" value={d.charges?.futures} />
            </div>
          </Section>

          {/* Financials */}
          <Section icon={DollarSign} title="Financial Performance">
            <div className="grid grid-cols-3 gap-4">
              <StatItem label="Revenue" value={d.financials?.revenue} />
              <StatItem label="Profit" value={d.financials?.profit} />
              <StatItem label="Market Cap" value={d.financials?.marketCap} />
            </div>
          </Section>

          {/* Complaints & Holding */}
          <div className="grid grid-cols-1 gap-6">
            <Section icon={AlertCircle} title="Complaints Info">
              <div className="grid grid-cols-2 gap-4">
                <StatItem label="Total Complaints" value={d.complaints?.total} />
                <StatItem label="Resolved" value={d.complaints?.resolved} />
              </div>
            </Section>
            
            <Section icon={PieChart} title="Shareholding">
              <div className="grid grid-cols-2 gap-4">
                <StatItem label="Promoters" value={d.shareHolding?.promoters} />
                <StatItem label="Public" value={d.shareHolding?.public} />
              </div>
            </Section>
          </div>
        </div>

        {/* Pros & Cons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Section icon={CheckCircle2} title="Pros">
            <ul className="space-y-2">
              {Array.isArray(d.pros) && d.pros.map((p, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-success-500 mt-1">•</span> {p}
                </li>
              ))}
            </ul>
          </Section>
          <Section icon={XCircle} title="Cons">
            <ul className="space-y-2">
              {Array.isArray(d.cons) && d.cons.map((c, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-danger-500 mt-1">•</span> {c}
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {/* Features */}
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-3 text-primary-600">
            <CheckCircle2 className="w-4 h-4" />
            <h3 className="text-sm font-bold uppercase tracking-wider">Available Features</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {Array.isArray(d.features) && d.features.map((f, i) => (
              <span key={i} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-700 shadow-sm">
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BrokerDetailsModal;
